import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import HookForm from '../../../../components/HookForm/HookForm';
import Button from '../../../../components/Button/Button';
import Loader from '../../../../components/Loader/Loader';
import LoaderInline from '../../../../components/Loader/LoaderInline';
import formStyles from '../../../../components/Form/form.module.css';

import SellerBlock from './SellerBlock';
import { buildCarInfoInputs } from '../carInfoInputs';
import ensureComplectation from '../ensureComplectation';
import useIbanAutofill from '../../../../hooks/useIbanAutofill';
import { shortFormsEnabled } from '../../../../brand/tenant';

import createCar from '../../../../api/cars/createCar';
import createConsignment from '../../../../api/cars/createConsignment';
import getDcRequisites from '../../../../api/cars/getDcRequisites';
import getDcList from '../../../../api/cars/getDcList';
import ensureContainer from '../../../../api/cars/ensureContainer';
import getItems from '../../../../api/cars/getItems';

import alert from '../../../../helpers/alert';
import extractErrorMessage from '../../../../utils/extractErrorMessage';
import { CAR_ORIGIN_TYPES, originLabel } from '../../../../constants/carOrigin';
import Select from '../../../../components/Select/Select';
import { textStyles } from '../../../../constants/textStyles';
import { consignmentAnyDcEnabled } from '../../../../brand/tenant';
import {
  REQUISITES_FIELDS,
  REQUISITES_INPUTS,
  SELLER_REQUISITES_INPUTS,
  agentRequisitesComplete,
  agentRequisitesOf,
  autoSellerDc,
  buildSellerDcInput,
  cleanBank,
  hideInputs,
  requisitesTitle,
  sellerAccountComplete,
  sellerAccountOf,
  sellerFio,
} from './requisitesInputs';

// minmax(0, 1fr), Ð° Ð½Ðµ 1fr: Ð¸Ð½Ð°ÑÐµ Ð´Ð»Ð¸Ð½Ð½Ð¾Ðµ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ (Â«ÐÐ¾ Ð´Ð¾Ð³Ð¾Ð²Ð¾ÑÑ Ð¿Ð¾ÑÑÑÐµÐ½Ð¸Ñ, Ð¿ÐµÑÐ²Ð¸ÑÐ½Ð°Ñ ÑÐµÐ³Ð¸ÑÑÑÐ°ÑÐ¸ÑÂ»)
// ÑÐ°Ð·Ð´Ð²Ð¸Ð³Ð°ÐµÑ ÑÐ²Ð¾Ñ ÐºÐ¾Ð»Ð¾Ð½ÐºÑ Ð¸ ÑÐ´Ð²Ð¸Ð³Ð°ÐµÑ Ð¾ÑÑÐ°Ð»ÑÐ½ÑÐµ.
const carGrid = {
  display: 'grid',
  gap: '20px',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  alignItems: 'flex-end',
};
// ÐÐ° ÑÐºÑÐ°Ð½Ðµ ÑÐ´ÐµÐ»ÐºÐ¸ ÑÐ¾ÑÐ¼Ð° ÑÐ¶Ðµ ÐºÐ¾Ð»Ð¾Ð½ÐºÐ¸: Ð² ÑÐµÑÑÑÐµ ÑÑÐ´Ð° Ð¿Ð¾Ð´Ð¿Ð¸ÑÐ¸ Ð¸ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ Ð½Ðµ Ð¿Ð¾Ð¼ÐµÑÐ°Ð»Ð¸ÑÑ.
const carGridEmbedded = { ...carGrid, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' };
const reqGrid = { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr 1fr' };
const reqOverlay = {
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  background: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * ÐÐ¾ÑÑÐ°Ð½Ð¾Ð²ÐºÐ° Ð±/Ñ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ Ð¿Ð¾ Ð´Ð¾Ð³Ð¾Ð²Ð¾ÑÑ Ð¿Ð¾ÑÑÑÐµÐ½Ð¸Ñ â Ð¾Ð´Ð¸Ð½ ÑÐºÑÐ°Ð½ Ð±ÐµÐ· ÑÐ°Ð³Ð¾Ð²:
 * Ð´Ð°Ð½Ð½ÑÐµ Ð¢Ð¡, Ð¿ÑÐ¾Ð´Ð°Ð²ÐµÑ (ÐºÐ»Ð¸ÐµÐ½Ñ CRM), ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ ÐÐ¦ Ð¸ ÑÑÑÑ Ð¿ÑÐ¾Ð´Ð°Ð²ÑÐ°.
 * Ð£ ÑÐµÑÐ¾Ð³Ð¾ Ð´Ð¸Ð»ÐµÑÐ° Ð¿ÑÐ¾Ð´Ð°Ð²ÐµÑ â ÑÐ°Ð¼ ÐÐ¦: Ð¾Ð´Ð¸Ð½ ÑÐµÐ»ÐµÐºÑ Ð²Ð¼ÐµÑÑÐ¾ ÐºÐ»Ð¸ÐµÐ½ÑÐ° Ð¸ ÑÑÑÑÐ°, Ð°Ð³ÐµÐ½Ñ â ÑÐ¾Ñ Ð¶Ðµ ÐÐ¦,
 * ÐµÐ³Ð¾ ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ Ð¿Ð¾Ð´ÑÑÐ°Ð²Ð»ÐµÐ½Ñ Ð¸Ð· ÐºÐ°ÑÑÐ¾ÑÐºÐ¸, Ð¿Ð¾ÑÑÐ¾Ð¼Ñ Ð±Ð»Ð¾ÐºÐ¸ ÑÑÑÑÐ° Ð¸ ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÐ¾Ð² ÑÐºÑÑÑÑ.
 * ÐÐ¾ÑÐ»Ðµ ÑÐ¾ÑÑÐ°Ð½ÐµÐ½Ð¸Ñ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ ÑÑÐ°Ð·Ñ Ð¿ÐµÑÐµÑÐ¾Ð´Ð¸Ñ Ðº ÑÐ¾Ð·Ð´Ð°Ð½Ð¸Ñ ÑÐ´ÐµÐ»ÐºÐ¸; ÐµÑÐ»Ð¸ ÑÐºÑÐ°Ð½ Ð¾ÑÐºÑÑÑ Ð¿Ð¾Ð²ÐµÑÑ
 * ÑÐ´ÐµÐ»ÐºÐ¸ (onCreated), Ð¾Ð½, Ð½Ð°Ð¾Ð±Ð¾ÑÐ¾Ñ, Ð¾ÑÐ´Ð°ÑÑ ÐµÐ¹ ÑÐ¾Ð·Ð´Ð°Ð½Ð½Ð¾Ðµ Ð°Ð²ÑÐ¾ Ð¸ Ð½Ð¸ÑÐµÐ³Ð¾ Ð½Ðµ ÑÐ²Ð¾Ð´Ð¸Ñ ÑÐ¾ ÑÑÑÐ°Ð½Ð¸ÑÑ.
 */
/**
 * embedded â ÑÐ¾ÑÐ¼Ð° Ð²ÑÑÑÐ¾ÐµÐ½Ð° Ð² ÑÐºÑÐ°Ð½ ÑÐ´ÐµÐ»ÐºÐ¸: Ð±ÐµÐ· ÑÐ²Ð¾ÐµÐ¹ ÐºÐ½Ð¾Ð¿ÐºÐ¸ Ð¸ Ð±ÐµÐ· Ð¿Ð¾Ð»Ñ VIN (ÐµÐ³Ð¾ Ð²Ð²Ð¾Ð´Ð¸Ñ ÑÐºÑÐ°Ð½ ÑÐ´ÐµÐ»ÐºÐ¸ Ð¸
 * Ð¿ÐµÑÐµÐ´Ð°ÑÑ Ð² vin), ÑÐ¾ÑÑÐ°Ð½ÐµÐ½Ð¸Ðµ Ð·Ð°Ð¿ÑÑÐºÐ°ÐµÑÑÑ ÑÐ½Ð°ÑÑÐ¶Ð¸ ÑÐµÑÐµÐ· ref.submit(), ÐºÐ¾ÑÐ¾ÑÑÐ¹ Ð¾ÑÐ´Ð°ÑÑ ÑÐ¾Ð·Ð´Ð°Ð½Ð½Ð¾Ðµ Ð°Ð²ÑÐ¾ Ð¸Ð»Ð¸ null.
 */
const ConsignmentCreate = forwardRef(({ originInput, originCarId = CAR_ORIGIN_TYPES.CONSIGNMENT, onCreated, vin, onVinChange, embedded = false }, ref) => {
  const navigate = useNavigate();
  const sellerIsDc = shortFormsEnabled();

  const carFormRef = useRef(null);
  const reqFormRef = useRef(null);
  const sellerReqFormRef = useRef(null);
  const sellerIbanAutofill = useIbanAutofill(sellerReqFormRef);
  // Ð ÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ ÐÐ¦ Ð¾Ð±ÑÑÐ½Ð¾ Ð¿ÑÐ¸ÐµÐ·Ð¶Ð°ÑÑ Ñ Ð±ÑÐºÐ° ÑÐµÐ»Ð¸ÐºÐ¾Ð¼; Ð¿Ð¾Ð´ÑÑÐ°Ð½Ð¾Ð²ÐºÐ° ÑÑÐ°Ð±Ð¾ÑÐ°ÐµÑ
  // ÑÐ¾Ð»ÑÐºÐ¾ ÑÐ°Ð¼, Ð³Ð´Ðµ Ð±Ð°Ð½Ðº Ð¸ ÐÐÐ Ð¾ÑÑÐ°Ð»Ð¸ÑÑ Ð¿ÑÑÑÑÐ¼Ð¸.
  const dcIbanAutofill = useIbanAutofill(reqFormRef);
  const sellerRef = useRef(null);

  const [containerId, setContainerId] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sellerError, setSellerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingRequisites, setLoadingRequisites] = useState(false);
  // ÐÑÐº Ð¿Ð¾Ð´ÑÑÐ°Ð²Ð¸Ð» ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ Ð¿Ð¾ÑÑÐµÐ´Ð½Ð¸ÐºÐ° ÐºÐ¾Ð½ÑÑÑÐ° Ð²Ð¼ÐµÑÑÐ¾ ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÐ¾Ð² Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ð³Ð¾ ÐÐ¦
  const [viaIntermediary, setViaIntermediary] = useState(false);
  const [agentRequisites, setAgentRequisites] = useState(null);
  const [carId, setCarId] = useState(null);
  // ÐÐ¾ÑÐ¾Ð´ Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ð³Ð¾ ÐÐ¦: Ñ ÑÐµÑÐ¾Ð³Ð¾ Ð´Ð¸Ð»ÐµÑÐ° Ð¾Ð½ Ð¶Ðµ Ð³Ð¾ÑÐ¾Ð´ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ, Ð¿Ð¾Ð»Ðµ Â«ÐÐ¾ÑÐ¾Ð´Â» ÑÐºÑÑÑÐ¾.
  const [dcCityId, setDcCityId] = useState(null);
  // Ð¡ÑÑÑ ÐÐ¦ Ð¸Ð· ÐµÐ³Ð¾ ÐºÐ°ÑÑÐ¾ÑÐºÐ¸ Ð¿ÑÐ¸ÑÑÐ» ÑÐµÐ»Ð¸ÐºÐ¾Ð¼ â Ð¿Ð¾Ð»Ñ ÑÑÑÑÐ° Ð¿ÑÑÑÐµÐ¼; Ð½ÐµÐ¿Ð¾Ð»Ð½ÑÐ¹ â Ð¿Ð¾ÐºÐ°Ð·ÑÐ²Ð°ÐµÐ¼, Ð´Ð¾Ð·Ð°Ð¿Ð¾Ð»Ð½Ð¸ÑÑ Ð¿ÑÐ¸Ð´ÑÑÑÑ ÑÑÐºÐ°Ð¼Ð¸.
  const [sellerAccountShown, setSellerAccountShown] = useState(false);
  // ÐÐ¦ Ð¿Ð¾Ð´Ð¾Ð±ÑÐ°Ð½ ÑÐ°Ð¼ (Ñ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑÐ° Ð¾Ð½ Ð¾Ð´Ð¸Ð½, Ð½Ðµ ÑÑÐ¸ÑÐ°Ñ Ð¿Ð¾ÑÑÐµÐ´Ð½Ð¸ÐºÐ°) â ÑÐµÐ»ÐµÐºÑ Ð¿ÑÑÑÐµÐ¼.
  const [dcAuto, setDcAuto] = useState(false);
  // ÐÐ¾ÐºÐ° ÑÐ¿Ð¸ÑÐ¾Ðº ÐÐ¦ Ð½Ðµ Ð·Ð°Ð³ÑÑÐ·Ð¸Ð»ÑÑ, Ð½Ðµ Ð·Ð½Ð°ÐµÐ¼, Ð½ÑÐ¶ÐµÐ½ Ð»Ð¸ ÑÐµÐ»ÐµÐºÑ: Ð±Ð»Ð¾Ðº Ð¿ÑÐ¾Ð´Ð°Ð²ÑÐ° Ð½Ðµ Ð¿Ð¾ÐºÐ°Ð·ÑÐ²Ð°ÐµÐ¼, ÑÑÐ¾Ð±Ñ Ð¾Ð½ Ð½Ðµ Ð¼Ð¸Ð³Ð°Ð».
  const [dcResolved, setDcResolved] = useState(!sellerIsDc);

  // Ð ÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ Ð±ÐµÑÑÐ¼ ÑÐ¾Ð²Ð½Ð¾ Ñ Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ð³Ð¾ ÐÐ¦ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ â Ð½Ð¸ÑÐµÐ³Ð¾ Ð½Ðµ Ð¿Ð¾Ð´ÑÑÐ°Ð²Ð»ÑÐµÐ¼
  // Ð¿Ð¾ ÐÐ¦ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑÐ°, Ð¿Ð¾ÐºÐ° Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ ÑÐ°Ð¼ Ð½Ðµ Ð²ÑÐ±ÑÐ°Ð» ÐÐ¦.
  const handleDcChange = async (selected) => {
    const list = Array.isArray(selected) ? selected : [selected];
    const dcId = list[list.length - 1]?.value;
    if (!dcId) return;
    setDcCityId(list[list.length - 1]?.cityId ?? null);

    setLoadingRequisites(true);
    const res = await getDcRequisites(dcId).catch(() => null);
    const requisites = res?.data || {};
    // Ð¡ÐµÑÑÐ¹ Ð´Ð¸Ð»ÐµÑ Ð¿ÑÐ¾Ð´Ð°ÑÑ ÑÐ°Ð¼ Ð¸ Ð°Ð³ÐµÐ½Ñ Ñ Ð½ÐµÐ³Ð¾ ÑÐ¾Ñ Ð¶Ðµ ÐÐ¦: Ð±ÐµÑÑÐ¼ ÐµÐ³Ð¾ ÑÐ¾Ð±ÑÑÐ²ÐµÐ½Ð½ÑÐµ ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ, Ð½Ðµ Ð¿Ð¾ÑÑÐµÐ´Ð½Ð¸ÐºÐ°.
    const agentSource = sellerIsDc ? requisites.own || {} : requisites;
    setViaIntermediary(sellerIsDc ? false : !!requisites.viaIntermediary);
    setAgentRequisites(agentRequisitesOf(agentSource));
    Object.entries(REQUISITES_FIELDS).forEach(([field, key]) =>
      reqFormRef.current?.setValue(field, agentSource?.[key] ?? '')
    );
    // ÐÑÐ¾Ð´Ð°Ð²ÐµÑ â ÑÑÐ¾Ñ Ð¶Ðµ ÐÐ¦: ÐµÐ³Ð¾ ÑÑÑÑ Ð¿Ð¾Ð´ÑÑÐ°Ð²Ð»ÑÐµÐ¼ Ð² ÑÐ¾ÑÐ¼Ñ ÑÑÐ°Ð·Ñ, Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ Ð¼Ð¾Ð¶ÐµÑ Ð¿Ð¾Ð¿ÑÐ°Ð²Ð¸ÑÑ.
    if (sellerIsDc) {
      Object.entries(sellerAccountOf(requisites.own)).forEach(([field, value]) =>
        sellerReqFormRef.current?.setValue(field, value)
      );
      setSellerAccountShown(!sellerAccountComplete(requisites.own));
    }
    setLoadingRequisites(false);
  };

  // ÐÐ¾Ð¼Ð¿Ð»ÐµÐºÑÐ°ÑÐ¸Ð¸ Ð²Ð¸ÑÑÑ Ð½Ð° ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑÐµ (Ð¿Ð°ÑÐ° Ð¼Ð°ÑÐºÐ°+Ð¼Ð¾Ð´ÐµÐ»Ñ), Ð° Ñ Ð±/Ñ-Ð¼Ð°ÑÐ¾Ðº Ð¸
  // Ð¼Ð¾Ð´ÐµÐ»ÐµÐ¹, Ð·Ð°Ð²ÐµÐ´ÑÐ½Ð½ÑÑ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑÐ¾Ð¼ Ð·Ð´ÐµÑÑ Ð¶Ðµ, ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑÐ° ÐµÑÑ Ð½ÐµÑ â Ð±ÑÐº Ð¾ÑÐ´Ð°ÑÑ
  // ÑÑÑÐµÑÑÐ²ÑÑÑÐ¸Ð¹ Ð¸Ð»Ð¸ Ð·Ð°Ð²Ð¾Ð´Ð¸Ñ Ð½ÐµÐ´Ð¾ÑÑÐ°ÑÑÐ¸Ð¹.
  // ponytail: ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑ Ð·Ð°Ð²Ð¾Ð´Ð¸ÑÑÑ ÑÑÐ°Ð·Ñ Ð¿ÑÐ¸ Ð²ÑÐ±Ð¾ÑÐµ Ð¼Ð¾Ð´ÐµÐ»Ð¸, Ð¿Ð¾ÑÑÐ¾Ð¼Ñ Ð±ÑÐ¾ÑÐµÐ½Ð½Ð°Ñ
  // ÑÐ¾ÑÐ¼Ð° Ð¾ÑÑÐ°Ð²Ð»ÑÐµÑ Ð¿ÑÑÑÐ¾Ð¹ ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑ. Ð Ð²Ð¸ÑÑÐ¸Ð½Ñ Ð¾Ð½ Ð½Ðµ Ð¿Ð¾Ð¿Ð°Ð´Ð°ÐµÑ (ÑÐ°Ð¼ ÑÐ¸Ð»ÑÑÑ Ð¿Ð¾
  // ÑÑÐ¾ÐºÑ), ÑÐ¸ÑÑÐ¸ÑÑ â ÑÐ¾Ð»ÑÐºÐ¾ ÐµÑÐ»Ð¸ ÑÐ°ÐºÐ¸Ðµ Ð·Ð°Ð¿Ð¸ÑÐ¸ Ð½Ð°ÑÐ½ÑÑ Ð¼ÐµÑÐ°ÑÑ Ð² /cars/containers.
  const resolveContainerId = async (modelOption, markOption) => {
    if (!markOption?.value || !modelOption?.value) {
      setContainerId(null);
      return null;
    }
    try {
      const res = await ensureContainer({ markId: markOption.value, modelId: modelOption.value });
      const id = res?.data?.id ?? null;
      setContainerId(id);
      return id;
    } catch (e) {
      // ÐÐ¾Ð»ÑÐ° Ð³Ð»Ð¾ÑÐ°ÑÑ Ð½ÐµÐ»ÑÐ·Ñ: Ð±ÐµÐ· ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑÐ° Ð¿ÑÑÑÐµÐµÑ ÑÐ¿Ð¸ÑÐ¾Ðº ÐºÐ¾Ð¼Ð¿Ð»ÐµÐºÑÐ°ÑÐ¸Ð¹ Ð¸
      // Ð¿ÑÐ¾Ð¿Ð°Ð´Ð°ÐµÑ ÐºÐ½Ð¾Ð¿ÐºÐ° Â«ÐÐ¾Ð±Ð°Ð²Ð¸ÑÑ ÐºÐ¾Ð¼Ð¿Ð»ÐµÐºÑÐ°ÑÐ¸ÑÂ» â Ð²ÑÐ³Ð»ÑÐ´Ð¸Ñ ÐºÐ°Ðº ÑÐ»Ð¾Ð¼Ð°Ð½Ð½Ð°Ñ
      // Ð²ÑÑÑÑÐºÐ°, ÑÐ¾ÑÑ Ð½Ð° Ð´ÐµÐ»Ðµ Ð½Ðµ Ð¾ÑÐ²ÐµÑÐ¸Ð» Ð±ÑÐº.
      setContainerId(null);
      alert.error(
        extractErrorMessage(e?.response?.data) ||
          'ÐÐµ ÑÐ´Ð°Ð»Ð¾ÑÑ Ð¾Ð¿ÑÐµÐ´ÐµÐ»Ð¸ÑÑ ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑ Ð¿Ð¾ Ð¼Ð°ÑÐºÐµ Ð¸ Ð¼Ð¾Ð´ÐµÐ»Ð¸ â ÐºÐ¾Ð¼Ð¿Ð»ÐµÐºÑÐ°ÑÐ¸Ð¸ Ð½ÐµÐ´Ð¾ÑÑÑÐ¿Ð½Ñ'
      );
      return null;
    }
  };

  // Ð£ ÑÐµÑÐ¾Ð³Ð¾ Ð´Ð¸Ð»ÐµÑÐ° ÑÐ¾ÑÐ¼Ð° Ð²ÑÐ³Ð»ÑÐ´Ð¸Ñ ÑÐ°Ðº Ð¶Ðµ, ÐºÐ°Ðº Ð¾Ð±ÑÑÐ½Ð°Ñ: Ð¿ÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ðµ Ð² ÑÐ¾Ð¹ Ð¶Ðµ ÑÐµÑÐºÐµ ÑÑÐ°Ð·Ñ Ð·Ð° VIN,
  // Ð° Ð½Ðµ Ð¾ÑÐ´ÐµÐ»ÑÐ½Ð¾Ð¹ ÐºÐ°ÑÑÐ¾ÑÐºÐ¾Ð¹ ÑÐ²ÐµÑÑÑ â Ð¿ÑÐ¸ Ð²ÑÐ±Ð¾ÑÐµ Ð¿ÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ñ Ð½Ð¸ÑÐµÐ³Ð¾ Ð½Ðµ Ð¿ÑÑÐ³Ð°ÐµÑ. ÐÐ±ÑÐ°Ð±Ð¾ÑÑÐ¸Ðº Ð±ÐµÑÑÐ¼
  // Ð¸Ð· ref: Ð¸Ð½Ð¿ÑÑÑ ÑÐ¾Ð±ÑÐ°Ð½Ñ Ð¾Ð´Ð¸Ð½ ÑÐ°Ð·, Ð° Ð·Ð°Ð¼ÑÐºÐ°Ð½Ð¸Ðµ CarCreate Ð¾Ð±Ð½Ð¾Ð²Ð»ÑÐµÑÑÑ ÐºÐ°Ð¶Ð´ÑÐ¹ ÑÐµÐ½Ð´ÐµÑ.
  const originInputRef = useRef(originInput);
  originInputRef.current = originInput;

  const carInputs = useMemo(() => {
    const inputs = buildCarInfoInputs({
        // VIN, Ð²Ð²ÐµÐ´ÑÐ½Ð½ÑÐ¹ Ð´Ð¾ Ð²ÑÐ±Ð¾ÑÐ° Ð¿ÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ñ, Ð¿ÐµÑÐµÐµÐ·Ð¶Ð°ÐµÑ ÑÑÐ´Ð° Ð²Ð¼ÐµÑÑÐµ Ñ ÑÐ¾ÑÐ¼Ð¾Ð¹.
        carData: { vin },
        onMarkChange: () => setContainerId(null),
        onModelChange: resolveContainerId,
        onDcChange: handleDcChange,
        singleDc: true,
        usedDcOnly: !consignmentAnyDcEnabled(),
        hideDc: sellerIsDc,
        // ÐÐ¾ÑÐ¾Ð´ Ð±ÐµÑÑÐ¼ Ñ ÐÐ¦; ÐµÑÐ»Ð¸ Ð² ÐµÐ³Ð¾ ÐºÐ°ÑÑÐ¾ÑÐºÐµ Ð³Ð¾ÑÐ¾Ð´Ð° Ð½ÐµÑ â Ð¿Ð¾Ð»Ðµ Ð¾ÑÑÐ°ÑÑÑÑ, Ð·Ð°Ð¿Ð¾Ð»Ð½Ð¸ÑÑ Ð¿ÑÐ¸Ð´ÑÑÑÑ ÑÑÐºÐ°Ð¼Ð¸.
        hideCity: sellerIsDc && !!dcCityId,
        vinFirst: true,
        onPickVinCar: pickExistingCar,
        getContainerId: () => containerId,
        withTechPassport: true,
        mileageRequired: true,
        usedDictionaries: true,
        shortFields: shortFormsEnabled(),
    });
    // ÐÑÑÑÐ¾ÐµÐ½Ð½Ð°Ñ ÑÐ¾ÑÐ¼Ð°: VIN Ð²Ð²Ð¾Ð´Ð¸Ñ ÑÐºÑÐ°Ð½ ÑÐ´ÐµÐ»ÐºÐ¸, Ð° Ð¿ÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ðµ Ð²ÑÐ±Ð¸ÑÐ°ÐµÑÑÑ Ð·Ð´ÐµÑÑ Ð¶Ðµ â Ð¸Ð· Ð´Ð²ÑÑ Ð²Ð°ÑÐ¸Ð°Ð½ÑÐ¾Ð² Ð¿Ð¾ÑÑÑÐµÐ½Ð¸Ñ.
    if (embedded) {
      const origin = {
        component: Select,
        name: 'origin',
        label: 'ÐÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ðµ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ',
        placeholder: 'ÐÑÐ±ÐµÑÐ¸ÑÐµ ÑÐ¸Ð¿ Ð¿ÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ñ',
        options: [CAR_ORIGIN_TYPES.CONSIGNMENT, CAR_ORIGIN_TYPES.CONSIGNMENT_FIRST_REG].map((value) => ({
          value,
          label: originLabel(value, true),
        })),
        defaultValue: { value: CAR_ORIGIN_TYPES.CONSIGNMENT, label: originLabel(CAR_ORIGIN_TYPES.CONSIGNMENT, true) },
        rules: { required: true },
      };
      return [origin, ...inputs.filter((input) => input.name !== 'vin')];
    }
    if (!sellerIsDc || !originInput) return inputs;
    const origin = {
      ...originInput,
      onChangeCustom: (...args) => originInputRef.current?.onChangeCustom?.(...args),
    };
    return [inputs[0], origin, ...inputs.slice(1)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, dcCityId]);

  const sellerInputs = useMemo(
    () =>
      sellerIsDc
        ? [
            ...hideInputs([buildSellerDcInput({ onChange: handleDcChange, used: !consignmentAnyDcEnabled() })], dcAuto),
            ...hideInputs(SELLER_REQUISITES_INPUTS, !sellerAccountShown),
          ]
        : SELLER_REQUISITES_INPUTS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sellerAccountShown, dcAuto]
  );

  // Ð¡ÐµÑÐ¾Ð¼Ñ Ð´Ð¸Ð»ÐµÑÑ Ð²ÑÐ±Ð¸ÑÐ°ÑÑ ÐÐ¦ Ð½Ðµ Ð½ÑÐ¶Ð½Ð¾: Ð±ÐµÑÑÐ¼ ÐµÐ³Ð¾ ÑÐ¾Ð±ÑÑÐ²ÐµÐ½Ð½ÑÐ¹, ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ Ð¿Ð¾Ð´ÑÑÐ½ÑÑÑÑ ÑÐ°Ð¼Ð¸.
  useEffect(() => {
    if (!sellerIsDc) return;
    getDcList({ used: !consignmentAnyDcEnabled() || undefined })
      .then((res) => {
        const auto = autoSellerDc(res?.data);
        if (!auto) return;
        setDcAuto(true);
        sellerReqFormRef.current?.setValue('dc', auto);
        handleDcChange(auto);
      })
      .catch(() => {})
      .finally(() => setDcResolved(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ð£ ÑÐµÑÐ¾Ð³Ð¾ Ð´Ð¸Ð»ÐµÑÐ° ÑÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ ÐÐ³ÐµÐ½ÑÐ° â ÑÑÐ¾ ÐºÐ°ÑÑÐ¾ÑÐºÐ° ÑÐ¾Ð³Ð¾ Ð¶Ðµ ÐÐ¦, Ð¸Ð· Ð½ÐµÑ Ð¾Ð½Ð¸ Ð¿Ð¾Ð´ÑÑÐ°Ð²Ð»ÐµÐ½Ñ: Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑÑ Ð¿Ð¾ÐºÐ°Ð·ÑÐ²Ð°ÑÑ
  // Ð¸ Ð¿ÑÐ°Ð²Ð¸ÑÑ ÑÐ°Ð¼ Ð½ÐµÑÐµÐ³Ð¾. ÐÐ»Ð¾Ðº Ð¿Ð¾ÑÐ²Ð»ÑÐµÑÑÑ, ÑÐ¾Ð»ÑÐºÐ¾ ÐºÐ¾Ð³Ð´Ð° ÐºÐ°ÑÑÐ¾ÑÐºÐ° Ð·Ð°Ð³ÑÑÐ¶ÐµÐ½Ð° Ð¸ Ð² Ð½ÐµÐ¹ ÑÐµÐ³Ð¾-ÑÐ¾ Ð½Ðµ ÑÐ²Ð°ÑÐ°ÐµÑ.
  const hideAgentBlock = sellerIsDc && !(agentRequisites && !agentRequisitesComplete(agentRequisites));

  const validateCar = (values, resolvedContainerId) => {
    if (values?.vin?.trim()?.length !== 17) {
      alert.error('ÐÐ»Ð¸Ð½Ð° VIN Ð´Ð¾Ð»Ð¶Ð½Ð° Ð±ÑÑÑ 17 ÑÐ¸Ð¼Ð²Ð¾Ð»Ð¾Ð²');
      return false;
    }
    if (values?.issueYear > new Date().getFullYear() || values?.issueYear < 1900) {
      alert.error('ÐÑÐ¾Ð²ÐµÑÑÑÐµ Ð³Ð¾Ð´ Ð²ÑÐ¿ÑÑÐºÐ° Ð°Ð²ÑÐ¾');
      return false;
    }
    if (!resolvedContainerId) {
      alert.error('ÐÐµ ÑÐ´Ð°Ð»Ð¾ÑÑ Ð¾Ð¿ÑÐµÐ´ÐµÐ»Ð¸ÑÑ ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑ Ð¿Ð¾ Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ð¹ Ð¼Ð°ÑÐºÐµ/Ð¼Ð¾Ð´ÐµÐ»Ð¸');
      return false;
    }
    return true;
  };

  const saveCar = async (values, resolvedContainerId, sellerDcId) => {
    if (carId) return carId;

    // ÐÐ° ÐºÐ¾ÑÐ¾ÑÐºÐ¾Ð¹ ÑÐ¾ÑÐ¼Ðµ ÐºÐ¾Ð¼Ð¿Ð»ÐµÐºÑÐ°ÑÐ¸Ð¸ Ð½Ð° ÑÐºÑÐ°Ð½Ðµ Ð½ÐµÑ: Ð¿Ð¾Ð´Ð±Ð¸ÑÐ°ÐµÐ¼ ÐµÑ Ð¿Ð¾ ÑÐ¸Ð¿Ñ
    // Ð´Ð²Ð¸Ð³Ð°ÑÐµÐ»Ñ ÑÐ°Ð¼Ð¸, Ð¸Ð½Ð°ÑÐµ Ð¼Ð°ÑÐ¸Ð½Ñ Ð½Ðµ Ð´Ð¾Ð±Ð°Ð²Ð¸ÑÑ Ð² ÑÐ´ÐµÐ»ÐºÑ.
    const complectationId = shortFormsEnabled()
      ? await ensureComplectation({
          containerId: resolvedContainerId,
          engineType: values?.engineType?.value,
        })
      : values?.complectationId?.value;

    const payload = {
      phid: 5,
      originCarId: values?.origin?.value ?? originCarId,
      used: true,
      containerId: resolvedContainerId,
      markId: values?.mark?.value,
      modelId: values?.model?.value,
      complectationId,
      transmissionId: values?.transmissionId?.value,
      issueYear: values?.issueYear,
      vin: values?.vin?.trim(),
      color: values?.color?.value,
      price: parseInt(String(values?.price).replace(/[^\d]/g, ''), 10),
      engine_volume: values?.engine_volume,
      powerId: values?.power,
      wdId: values?.wdId?.value,
      cityId: values?.cityId?.value ?? dcCityId ?? undefined,
      dcIds: sellerDcId
        ? [sellerDcId]
        : []
            .concat(values?.dc || [])
            .map((dc) => dc?.value)
            .filter((v) => v != null),
      mileage: values?.mileage ? parseInt(values.mileage, 10) : undefined,
      engineType: values?.engineType?.value,
      texNumber: values?.texNumber?.trim() || undefined,
      texDate: values?.texDate ? dayjs(values.texDate).format('YYYY-MM-DD') : undefined,
      paid: 5124,
      paidTypeId: 1,
      countryId: 113,
    };

    const data = new FormData();
    data.append('json', JSON.stringify(payload));

    const response = await createCar(data);
    const id = response?.data?.car?.id || response?.data?.id;
    setCarId(id);
    return id;
  };

  const handleSubmit = async () => {
    if (sellerIsDc && loadingRequisites) {
      alert.error('Ð ÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ Ð´Ð¸Ð»ÐµÑÑÐºÐ¾Ð³Ð¾ ÑÐµÐ½ÑÑÐ° ÐµÑÑ Ð·Ð°Ð³ÑÑÐ¶Ð°ÑÑÑÑ â Ð¿Ð¾Ð²ÑÐ¾ÑÐ¸ÑÐµ ÑÐµÑÐµÐ· ÑÐµÐºÑÐ½Ð´Ñ');
      return null;
    }
    if (!sellerIsDc) {
      if (!seller?.value) {
        setSellerError('ÐÑÐ±ÐµÑÐ¸ÑÐµ Ð¸Ð»Ð¸ ÑÐ¾Ð·Ð´Ð°Ð¹ÑÐµ Ð¿ÑÐ¾Ð´Ð°Ð²ÑÐ°');
        sellerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return null;
      }
      setSellerError('');
    }

    const sellerRequisites = await sellerReqFormRef.current.submit();
    if (!sellerRequisites) return null;
    const sellerDc = sellerIsDc ? sellerRequisites.dc : null;

    const submittedCar = await carFormRef.current.submit();
    if (!submittedCar) return null;
    const carValues = embedded ? { ...submittedCar, vin } : submittedCar;

    // ÐÐµÐ½ÐµÐ´Ð¶ÐµÑ Ð¼Ð¾Ð¶ÐµÑ Ð½Ð°Ð¶Ð°ÑÑ Â«Ð¡Ð¾ÑÑÐ°Ð½Ð¸ÑÑÂ» ÑÐ°Ð½ÑÑÐµ, ÑÐµÐ¼ Ð¾ÑÐ²ÐµÑÐ¸Ñ ensureContainer Ð¿Ð¾
    // ÑÐ¾Ð»ÑÐºÐ¾ ÑÑÐ¾ Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ð¹ Ð¼Ð¾Ð´ÐµÐ»Ð¸ â Ð´Ð¾Ð¶Ð¸Ð¼Ð°ÐµÐ¼ ÐºÐ¾Ð½ÑÐµÐ¹Ð½ÐµÑ Ð·Ð´ÐµÑÑ, Ð° Ð½Ðµ Ð¿Ð°Ð´Ð°ÐµÐ¼ Ð²
    // Â«Ð½Ðµ ÑÐ´Ð°Ð»Ð¾ÑÑ Ð¾Ð¿ÑÐµÐ´ÐµÐ»Ð¸ÑÑÂ».
    const resolvedContainerId = containerId || (await resolveContainerId(carValues?.model, carValues?.mark));
    if (!validateCar(carValues, resolvedContainerId)) return null;

    let requisites = agentRequisites;
    if (hideAgentBlock) {
      if (loadingRequisites || !requisites) {
        alert.error('Ð ÐµÐºÐ²Ð¸Ð·Ð¸ÑÑ Ð´Ð¸Ð»ÐµÑÑÐºÐ¾Ð³Ð¾ ÑÐµÐ½ÑÑÐ° ÐµÑÑ Ð·Ð°Ð³ÑÑÐ¶Ð°ÑÑÑÑ â Ð¿Ð¾Ð²ÑÐ¾ÑÐ¸ÑÐµ ÑÐµÑÐµÐ· ÑÐµÐºÑÐ½Ð´Ñ');
        return null;
      }
    } else {
      requisites = await reqFormRef.current.submit();
      if (!requisites) return null;
    }

    setSaving(true);
    try {
      const id = await saveCar(carValues, resolvedContainerId, sellerDc?.value);
      if (!id) throw new Error('ÐÐµ ÑÐ´Ð°Ð»Ð¾ÑÑ ÑÐ¾Ð·Ð´Ð°ÑÑ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ');

      await createConsignment({
        carId: id,
        ...(sellerDc ? { sellerDcId: sellerDc.value } : { sellerClientId: seller.value }),
        price: parseInt(String(carValues?.price).replace(/[^\d]/g, ''), 10),
        requisites,
        sellerRequisites: {
          recipient: sellerRequisites.recipient,
          iban: cleanBank(sellerRequisites.iban),
          bankName: sellerRequisites.bankName,
          bic: cleanBank(sellerRequisites.bic),
        },
      });

      return (await goToDeal(id, sellerDc)) ?? null;
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || e?.message || 'ÐÐµ ÑÐ´Ð°Ð»Ð¾ÑÑ Ð¾ÑÐ¾ÑÐ¼Ð¸ÑÑ Ð¿Ð¾ÑÑÑÐµÐ½Ð¸Ðµ');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ÐÑÑÑÐ¾ÐµÐ½Ð½Ð°Ñ ÑÐ¾ÑÐ¼Ð° ÑÐ¾ÑÑÐ°Ð½ÑÐµÑÑÑ ÑÐ½Ð°ÑÑÐ¶Ð¸: ÑÐºÑÐ°Ð½ ÑÐ´ÐµÐ»ÐºÐ¸ Ð·Ð¾Ð²ÑÑ submit() Ð¸ Ð¿Ð¾Ð»ÑÑÐ°ÐµÑ ÑÐ¾Ð·Ð´Ð°Ð½Ð½Ð¾Ðµ Ð°Ð²ÑÐ¾ Ð¸Ð»Ð¸ null.
  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  // ÐÐµÐ½ÐµÐ´Ð¶ÐµÑ Ð²ÑÐ±ÑÐ°Ð» Ð² Ð¿Ð¾Ð´ÑÐºÐ°Ð·ÐºÐµ Ð¿Ð¾ VIN ÑÐ¶Ðµ Ð·Ð°Ð²ÐµÐ´ÑÐ½Ð½ÑÑ Ð¼Ð°ÑÐ¸Ð½Ñ â Ð²ÑÐ¾ÑÑÑ ÐºÐ°ÑÑÐ¾ÑÐºÑ Ð½Ðµ Ð·Ð°Ð²Ð¾Ð´Ð¸Ð¼,
  // ÑÐ²Ð¾Ð´Ð¸Ð¼ Ð² ÑÐ´ÐµÐ»ÐºÑ Ñ ÑÑÐ¾Ð¹.
  async function pickExistingCar(found) {
    const res = await getItems({ id: found.id }).catch(() => null);
    const car = res?.data?.cars?.[0];
    if (!car) {
      navigate(`/cars/car/${found.id}/main-data`);
      return;
    }
    // ÐÐ¾Ð²ÐµÑÑ ÑÐ´ÐµÐ»ÐºÐ¸ Ð¾ÑÐ´Ð°ÑÐ¼ Ð¼Ð°ÑÐ¸Ð½Ñ ÐµÐ¹ Ð¶Ðµ, Ð° Ð½Ðµ ÑÐ²Ð¾Ð´Ð¸Ð¼ ÑÐ¾ ÑÑÑÐ°Ð½Ð¸ÑÑ.
    if (onCreated) {
      onCreated(car);
      return;
    }
    navigate('/deals/create', { state: { car } });
  }

  // Ð¡ÑÐ°Ð·Ñ ÑÐ²Ð¾Ð´Ð¸Ð¼ Ð² ÑÐ¾Ð·Ð´Ð°Ð½Ð¸Ðµ ÑÐ´ÐµÐ»ÐºÐ¸ Ñ ÑÑÐ¸Ð¼ Ð°Ð²ÑÐ¾. ÐÐ°ÑÑÐ¾ÑÐºÑ ÑÑÐ½ÐµÐ¼ Ñ Ð±ÑÐºÐ°, ÑÑÐ¾Ð±Ñ
  // ÑÐºÑÐ°Ð½ ÑÐ´ÐµÐ»ÐºÐ¸ Ð¿Ð¾Ð»ÑÑÐ¸Ð» ÑÐ¾Ð²Ð½Ð¾ ÑÐ¾Ñ Ð¶Ðµ Ð¾Ð±ÑÐµÐºÑ, ÑÑÐ¾ Ð¸ Ð¿ÑÐ¸ Ð¿ÑÐ¾Ð´Ð°Ð¶Ðµ Ð¸Ð· ÐºÐ°ÑÑÐ¾ÑÐºÐ¸ Ð°Ð²ÑÐ¾.
  const goToDeal = async (id, sellerDc) => {
    const res = await getItems({ id }).catch(() => null);
    const car = res?.data?.cars?.[0];

    if (onCreated || embedded) {
      if (!car) {
        alert.error('ÐÐ²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ ÑÐ¾Ð·Ð´Ð°Ð½, Ð½Ð¾ Ð½Ðµ ÑÐ´Ð°Ð»Ð¾ÑÑ ÐµÐ³Ð¾ Ð·Ð°Ð³ÑÑÐ·Ð¸ÑÑ â Ð½Ð°Ð¹Ð´Ð¸ÑÐµ ÐµÐ³Ð¾ Ð² ÑÐ¿Ð¸ÑÐºÐµ Ð¸ Ð´Ð¾Ð±Ð°Ð²ÑÑÐµ Ð² ÑÐ´ÐµÐ»ÐºÑ');
        return null;
      }
      const created = {
        ...car,
        consignment:
          car?.consignment ||
          (sellerDc
            ? { sellerDc: { id: sellerDc.value, name: sellerDc.label } }
            : { seller: seller?.client || { name: seller?.label } }),
      };
      if (onCreated) onCreated(created);
      return created;
    }

    if (!car) {
      alert.success('ÐÐ²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ Ð¿ÑÐ¸Ð½ÑÑ Ð¿Ð¾ Ð´Ð¾Ð³Ð¾Ð²Ð¾ÑÑ Ð¿Ð¾ÑÑÑÐµÐ½Ð¸Ñ');
      navigate(`/cars/car/${id}/main-data`);
      return;
    }

    navigate('/deals/create', {
      state: {
        car: {
          ...car,
          consignment:
            car?.consignment ||
            (sellerDc
              ? { sellerDc: { id: sellerDc.value, name: sellerDc.label } }
              : { seller: seller?.client || { name: seller?.label } }),
        },
      },
    });
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Ð¤Ð¾ÑÐ¼Ñ Ð½Ðµ ÑÐ°Ð·Ð¼Ð¾Ð½ÑÐ¸ÑÑÐµÐ¼ Ð½Ð° Ð²ÑÐµÐ¼Ñ ÑÐ¾ÑÑÐ°Ð½ÐµÐ½Ð¸Ñ: Ð¿ÑÐ¸ Ð¾ÑÐ¸Ð±ÐºÐµ Ð±ÑÐºÐ° Ð²Ð²ÐµÐ´ÑÐ½Ð½Ð¾Ðµ
          Ð½Ðµ Ð´Ð¾Ð»Ð¶Ð½Ð¾ Ð¿ÑÐ¾Ð¿Ð°Ð´Ð°ÑÑ. */}
      {saving && !embedded && <Loader />}

      {/* ÐÑÐ¾Ð¸ÑÑÐ¾Ð¶Ð´ÐµÐ½Ð¸Ðµ Ð¾ÑÑÐ°ÑÑÑÑ Ð½Ð°Ð²ÐµÑÑÑ, Ð³Ð´Ðµ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ ÐµÐ³Ð¾ Ð¸ Ð²ÑÐ±ÑÐ°Ð». */}
      {originInput && !sellerIsDc && (
        <HookForm border inputs={[originInput]} grid={{ display: 'grid', maxWidth: 420 }} />
      )}

      {!sellerIsDc && (
        <div className={formStyles.borderWrapper} ref={sellerRef}>
          <p style={textStyles.formTitle}>1. ÐÑÐ¾Ð´Ð°Ð²ÐµÑ</p>
          <div style={{ marginTop: 20 }}>
            <SellerBlock
              seller={seller}
              onChange={(value) => {
                setSeller(value);
                setSellerError('');
                const fio = sellerFio(value);
                if (fio) sellerReqFormRef.current?.setValue('recipient', fio);
              }}
              error={sellerError}
            />
          </div>
        </div>
      )}

      {/* ÐÐ¦ Ð¿Ð¾Ð´Ð¾Ð±ÑÐ°Ð½ ÑÐ°Ð¼ Ð¸ ÑÑÑÑ Ð² ÐºÐ°ÑÑÐ¾ÑÐºÐµ Ð¿Ð¾Ð»Ð½ÑÐ¹ â Ð±Ð»Ð¾Ðº Ð¿ÑÑÑ, Ð½Ð¾ Ð¾ÑÑÐ°ÑÑÑÑ ÑÐ¼Ð¾Ð½ÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð½ÑÐ¼: Ð² Ð½ÑÐ¼ Ð»ÐµÐ¶Ð°Ñ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ. */}
      <div style={sellerIsDc && (!dcResolved || (dcAuto && !sellerAccountShown)) ? { display: 'none' } : undefined}>
        <HookForm
          ref={sellerReqFormRef}
          border
          title={sellerIsDc ? 'ÐÑÐ¾Ð´Ð°Ð²ÐµÑ â Ð´Ð¸Ð»ÐµÑÑÐºÐ¸Ð¹ ÑÐµÐ½ÑÑ' : '2. Ð¡ÑÑÑ Ð¿ÑÐ¾Ð´Ð°Ð²ÑÐ°'}
          inputs={sellerInputs}
          grid={reqGrid}
          onValuesChange={sellerIbanAutofill}
        />
      </div>

      <HookForm
        ref={carFormRef}
        border
        title={embedded ? 'ÐÐ°Ð½Ð½ÑÐµ Ð½Ð¾Ð²Ð¾Ð³Ð¾ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ' : sellerIsDc ? 'ÐÐ½ÑÐ¾ÑÐ¼Ð°ÑÐ¸Ñ Ð¾Ð± Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ðµ' : '3. ÐÐ°Ð½Ð½ÑÐµ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ'}
        inputs={carInputs}
        grid={embedded ? carGridEmbedded : carGrid}
        onValuesChange={(values) => onVinChange && onVinChange(values?.vin || '')}
      />

      {/* Ð¤Ð¾ÑÐ¼Ñ Ð½Ðµ Ð¿Ð¾Ð´Ð¼ÐµÐ½ÑÐµÐ¼ Ð»Ð¾Ð°Ð´ÐµÑÐ¾Ð¼: Ð¾Ð½Ð° Ð´Ð¾Ð»Ð¶Ð½Ð° Ð¾ÑÑÐ°ÑÑÑÑ ÑÐ¼Ð¾Ð½ÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð½Ð¾Ð¹, ÑÑÐ¾Ð±Ñ
          Ð¾ÑÐ²ÐµÑ Ð¿Ð¾ ÐÐ¦ Ð±ÑÐ»Ð¾ ÐºÑÐ´Ð° Ð·Ð°Ð¿Ð¸ÑÐ°ÑÑ. ÐÐ¼ÐµÑÑÐ¾ ÑÑÐ¾Ð³Ð¾ Ð³Ð°ÑÐ¸Ð¼ Ð±Ð»Ð¾Ðº Ð¾Ð²ÐµÑÐ»ÐµÐµÐ¼ â
          customDataTop ÑÐµÐ½Ð´ÐµÑÐ¸ÑÑÑ Ð²Ð½ÑÑÑÐ¸ ÐºÐ°ÑÑÐ¾ÑÐºÐ¸, Ð¿Ð¾ÑÑÐ¾Ð¼Ñ inset ÑÑÐ¸ÑÐ°ÐµÑÑÑ Ð¾Ñ Ð½ÐµÑ. */}
      {/* ÐÐ»Ð¾Ðº Ð¾ÑÑÐ°ÑÑÑÑ ÑÐ¼Ð¾Ð½ÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð½ÑÐ¼ Ð¸ Ð¿ÑÐ¾ÑÑÐ¾ ÑÐºÑÑÑ: Ð¾ÑÐ²ÐµÑ Ð¿Ð¾ ÐÐ¦ Ð·Ð°Ð¿Ð¸ÑÑÐ²Ð°ÐµÑÑÑ Ð² Ð½ÐµÐ³Ð¾ Ð² Ð»ÑÐ±Ð¾Ð¼ ÑÐ»ÑÑÐ°Ðµ. */}
      <div style={hideAgentBlock ? { display: 'none' } : undefined}>
        <HookForm
          ref={reqFormRef}
          border
          title={requisitesTitle(viaIntermediary)}
          inputs={REQUISITES_INPUTS}
          grid={reqGrid}
          onValuesChange={dcIbanAutofill}
          style={{ position: 'relative' }}
          customDataTop={
            loadingRequisites
              ? () => (
                  <div style={reqOverlay}>
                    <LoaderInline />
                  </div>
                )
              : undefined
          }
        />
      </div>

      {!embedded && (
        <Button
          text={onCreated ? 'Ð¡Ð¾ÑÑÐ°Ð½Ð¸ÑÑ Ð¸ Ð´Ð¾Ð±Ð°Ð²Ð¸ÑÑ Ð² ÑÐ´ÐµÐ»ÐºÑ' : 'Ð¡Ð¾ÑÑÐ°Ð½Ð¸ÑÑ Ð¸ Ð¿ÐµÑÐµÐ¹ÑÐ¸ Ðº ÑÐ´ÐµÐ»ÐºÐµ'}
          fill
          onClick={handleSubmit}
          disabled={saving}
        />
      )}
    </div>
  );
});

export default ConsignmentCreate;
